// ==================== src/api/system/points.ts ====================
// 对应后端：system.points.controller.PointsController
// 请求前缀：/api/points
import { post } from '@/utils/HttpUtils';
import type { PageQuery, PageResult } from '../common';

// ---------- 积分信息（嵌入 UserInfo） ----------
export interface PointsInfo {
  totalAvailablePoints: number;
  totalFrozenPoints: number;
  totalSpentPoints: number;
  totalEarnedPoints: number;
  accounts: AccountPoints[];
}

export interface AccountPoints {
  biliUid: number;
  totalPoints: number;
  frozenPoints: number;
  spentPoints: number;
  availablePoints: number;
}

// ---------- 积分汇总（钱包视图 / 管理员查看） ----------
export interface UserPointsSummary {
  userId: string;
  totalAvailablePoints: number;
  totalFrozenPoints: number;
  totalSpentPoints: number;
  totalEarnedPoints: number;
  accounts: AccountPointsDetail[];
  allFrozenDetails: FrozenDetail[];
}

export interface AccountPointsDetail {
  biliUid: number;
  totalPoints: number;
  frozenPoints: number;
  spentPoints: number;
  availablePoints: number;
  frozenDetails: FrozenDetail[];
}

export interface FrozenDetail {
  orderId: number;
  freezePoints: number;
  createTime: string;
}

// ---------- 积分变动记录 ----------
export interface PointsRecordItem {
  id: number;
  biliUid: number;
  /** 业务来源："GIFT" / "ORDER" / "ADMIN" */
  sourceCode: string;
  /** 交易类型枚举名，如 "FREEZE_PENDING" / "GRANT_DIRECT" / "ORDER_SPEND" */
  txType: string;
  /** 交易类型中文描述 */
  txTypeDesc: string;
  /** 业务 ID（礼物流水 ID / 订单 ID），ADMIN 为 null */
  bizId: number | null;
  /** 总积分变动 */
  deltaTotal: number;
  /** 冻结积分变动 */
  deltaFrozen: number;
  /** 已消费积分变动 */
  deltaSpent: number;
  /** 变动后的可用积分（= total - frozen - spent） */
  balanceAfter: number;
  description: string;
  createTime: string;
}

/**
 * 派生字段：可用积分变动。
 * 供展示层统一使用，避免各视图各自计算。
 */
export function getAvailableDelta(row: PointsRecordItem): number {
  return (row.deltaTotal ?? 0) - (row.deltaFrozen ?? 0) - (row.deltaSpent ?? 0);
}

export interface BiliAccountPageQuery extends PageQuery {
  biliUid?: number
  nickname?: string
  sortBy?: string
  sortDirection?: string
}

export interface BiliAccountPageItem {
  biliUid: number
  totalPoints: number
  frozenPoints: number
  spentPoints: number
  availablePoints: number
  nickname?: string
  boundUserId?: string
  boundUsername?: string
}

export const systemPointsApi = {
  /** 查询系统用户的积分汇总信息（各 B站账户及冻结明细） */
  getUserPointsSummary: (userId: string) =>
    post<UserPointsSummary>('/api/points/user/summary', { userId }),

  /** 按系统用户分页查询积分变动记录 */
  getUserPointsRecords: (userId: string, query: PageQuery) =>
    post<PageResult<PointsRecordItem>>('/api/points/records/user-page', {
      ...query,
      userId,
    }),

  /** 管理员积分调整（操作 B站账户） */
  adjustUserPoints: (data: {
    biliUid: number;
    change: number;
    description?: string;
  }) => post('/api/points/adjust', data),

  /** 积分记录查询（按 B站 UID，管理员） */
  getPointRecordsByBiliUid: (biliUid: number, query: PageQuery) =>
    post<PageResult<PointsRecordItem>>('/api/points/records/page', {
      ...query,
      biliUid,
    }),

  pageBiliAccounts: (query: BiliAccountPageQuery) =>
    post<PageResult<BiliAccountPageItem>>('/api/points/accounts/page', query),
};